import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { type ChangeEvent, useCallback, useEffect, useState } from "react";
import {
  CROWN_IMPORT_COPY,
  PRODUCTION_SAVE_HINT,
} from "../tenedi-connection-copy";
import useCrownImport, {
  type CrownImportReport,
  type CrownImportState,
} from "../use-crown-import";
import CrownImportReportView from "./crown-import-report";

const styles = {
  paper: { p: 2 } satisfies SxProps<Theme>,
  section: { mt: 2 } satisfies SxProps<Theme>,
  actions: { mt: 2, alignItems: "center" } satisfies SxProps<Theme>,
  hiddenInput: { display: "none" } satisfies SxProps<Theme>,
} as const;

/** What the card is waiting on, or null when it is not busy. */
const progressLabel = (step: CrownImportState["step"]): string | null => {
  switch (step) {
    case "uploading": {
      return CROWN_IMPORT_COPY.uploading;
    }
    case "planning":
    case "replanning": {
      return CROWN_IMPORT_COPY.planning;
    }
    case "applying": {
      return CROWN_IMPORT_COPY.applying;
    }
    case "idle":
    case "planned":
    case "applied": {
      return null;
    }
  }
};

type CrownImportCardProps = {
  readonly carrierUuid: string;
  /** False in production until the operator types `prod` in the banner. */
  readonly applyAllowed: boolean;
  readonly confirmProduction: boolean | undefined;
  /** Reports the backend's production flag from a plan, so the banner can ask for `prod`. */
  readonly onServerProduction: (production: boolean) => void;
  /** Reloads what the import changed; a rejection is shown on the card. */
  readonly onApplied: (report: CrownImportReport) => Promise<void>;
};

/**
 * Imports one carrier's Crown EDI export (TMS-3057): choosing the zip uploads
 * and plans it, which writes nothing, and Apply writes that plan. The backend
 * owns every rule and the production opt-in. The page keys it by carrier, so
 * another carrier starts a fresh card.
 */
const CrownImportCard = ({
  carrierUuid,
  applyAllowed,
  confirmProduction,
  onServerProduction,
  onApplied,
}: CrownImportCardProps) => {
  const { state, error, plan, setCutover, apply } = useCrownImport(carrierUuid);
  const [reloadError, setReloadError] = useState<string | null>(null);

  const report = "report" in state ? state.report : null;
  const progress = progressLabel(state.step);
  const busy = progress !== null;

  useEffect(() => {
    if (report !== null) {
      onServerProduction(report.production);
    }
  }, [onServerProduction, report]);

  const handleFile = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (file !== undefined) {
        setReloadError(null);
        void plan(file);
      }
    },
    [plan],
  );

  const handleCutover = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      void setCutover(event.target.checked);
    },
    [setCutover],
  );

  const handleApply = useCallback(() => {
    setReloadError(null);
    apply({ confirmProduction })
      .then(async (applied) => {
        if (applied !== null) {
          await onApplied(applied);
        }
      })
      .catch((error_: unknown) => {
        setReloadError(
          CROWN_IMPORT_COPY.reloadFailed(
            error_ instanceof Error ? error_.message : String(error_),
          ),
        );
      });
  }, [apply, confirmProduction, onApplied]);

  return (
    <Paper sx={styles.paper} variant="outlined">
      <Typography variant="h6">{CROWN_IMPORT_COPY.title}</Typography>
      <Typography color="text.secondary" variant="body2">
        {CROWN_IMPORT_COPY.subtitle}
      </Typography>
      <Stack direction="row" spacing={2} sx={styles.actions}>
        <Button component="label" disabled={busy} variant="outlined">
          {state.step === "idle"
            ? CROWN_IMPORT_COPY.chooseFile
            : CROWN_IMPORT_COPY.chooseAnother}
          <Box
            accept=".zip,application/zip"
            component="input"
            data-testid="crown-import-file"
            sx={styles.hiddenInput}
            type="file"
            onChange={handleFile}
          />
        </Button>
        {progress !== null && (
          <Stack alignItems="center" direction="row" spacing={1}>
            <CircularProgress size={20} />
            <Typography variant="body2">{progress}</Typography>
          </Stack>
        )}
      </Stack>
      {error !== null && (
        <Alert severity="error" sx={styles.section}>
          {error}
        </Alert>
      )}
      {reloadError !== null && (
        <Alert severity="warning" sx={styles.section}>
          {reloadError}
        </Alert>
      )}
      {report !== null && (
        <Box sx={styles.section}>
          <CrownImportReportView report={report} />
        </Box>
      )}
      {"confirmCutover" in state && (
        <Stack direction="row" spacing={2} sx={styles.actions}>
          <FormControlLabel
            control={
              <Checkbox
                checked={state.confirmCutover}
                disabled={busy}
                onChange={handleCutover}
              />
            }
            label={CROWN_IMPORT_COPY.cutover}
          />
          <Button
            disabled={busy || !applyAllowed}
            variant="contained"
            onClick={handleApply}
          >
            {CROWN_IMPORT_COPY.apply}
          </Button>
          {!applyAllowed && (
            <Typography color="error" variant="body2">
              {PRODUCTION_SAVE_HINT}
            </Typography>
          )}
        </Stack>
      )}
    </Paper>
  );
};

export default CrownImportCard;
